import { PublicClientApplication, type AccountInfo, type Configuration } from '@azure/msal-browser';
import { useAuth, getAuthToken } from './store';

/**
 * Entra ID (MSAL) sign-in for the firm-team surface — the production
 * counterpart to the dev-auth shortcut in `store.ts` (T-0010). Flow:
 *
 *   1. `initAuth()` runs at app boot: initializes MSAL, resolves any redirect
 *      coming back from Entra, and — if an account is present — silently
 *      acquires an access token for the API scope and populates the auth store.
 *   2. `signIn()` (login page button) → `loginRedirect` to Entra.
 *   3. `ensureFreshApiToken()` is called by the API client before each request;
 *      MSAL returns a cached token and auto-refreshes it near expiry, so the
 *      bearer stays valid without a per-request round-trip in the common case.
 *
 * The API validates the token's audience (`api://caliber`) and resolves the
 * internal user from the token's Entra object id — so the frontend does not
 * need to know the internal `users.id`; `identity.userId` here carries the
 * Entra object id for display/keying only.
 *
 * Config comes from BUILD-TIME Vite env vars (baked into the bundle):
 *   - VITE_ENTRA_CLIENT_ID   the app registration's Application (client) id
 *   - VITE_ENTRA_TENANT_ID   the directory (tenant) id
 *   - VITE_ENTRA_API_SCOPE   defaults to `api://caliber/access_as_user`
 *
 * When the client id / tenant id are absent (local dev), MSAL is inert and the
 * dev-auth / magic-link paths in `store.ts` take over.
 */

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined;
const apiScope =
  (import.meta.env.VITE_ENTRA_API_SCOPE as string | undefined) ?? 'api://caliber/access_as_user';

export function isMsalConfigured(): boolean {
  return Boolean(clientId && tenantId);
}

const msalConfig: Configuration = {
  auth: {
    clientId: clientId ?? '',
    authority: `https://login.microsoftonline.com/${tenantId ?? 'common'}`,
    // The SPA redirect URI registered in Entra must equal this origin.
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
    // Stay on the redirect URI (origin) after processing the response instead
    // of bouncing back to the page that started the login (/login). Without
    // this, a successful sign-in lands the user back on /login; the login
    // route's redirect-when-authenticated effect then moves them into the app.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // localStorage so the MSAL account + refresh token survive browser
    // restarts and new tabs. sessionStorage (the previous setting) wiped
    // the MSAL cache per-tab while the app's own identity persisted in
    // localStorage — a fresh tab LOOKED signed in but had no account to
    // silently refresh with, so every request carried an hours-old
    // expired access token and the API answered "JWT verification
    // failed." The refresh token is origin-bound and revocable in Entra;
    // persisting it here is the standard MSAL SPA configuration.
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

// Constructed lazily and ONLY when configured — MSAL throws on an empty
// clientId at construction, so we must not `new` it in unconfigured (dev)
// builds where this module is still imported by the API client.
let instance: PublicClientApplication | null = null;
function getInstance(): PublicClientApplication {
  if (!instance) {
    instance = new PublicClientApplication(msalConfig);
  }
  return instance;
}

let initialized = false;
async function ensureInitialized(): Promise<void> {
  if (initialized) return;
  await getInstance().initialize();
  initialized = true;
}

/** Push the acquired token + account identity into the shared auth store. */
function populateSession(accessToken: string, account: AccountInfo): void {
  useAuth.getState().setSession(accessToken, {
    // Entra object id — the API resolves the real internal user from the token.
    userId: account.localAccountId,
    email: account.username,
    displayName: account.name ?? account.username,
  });
}

/**
 * App-boot hook. Safe to call unconditionally: no-op when MSAL isn't configured
 * (local dev), otherwise resolves the redirect + hydrates the session.
 */
export async function initAuth(): Promise<void> {
  if (!isMsalConfigured()) return;
  await ensureInitialized();

  const redirectResult = await getInstance().handleRedirectPromise();
  const account =
    redirectResult?.account ??
    getInstance().getActiveAccount() ??
    getInstance().getAllAccounts()[0] ??
    null;
  if (!account) return; // not signed in — the login page offers the button.

  getInstance().setActiveAccount(account);
  try {
    const result = await getInstance().acquireTokenSilent({ scopes: [apiScope], account });
    populateSession(result.accessToken, account);
  } catch {
    // Silent acquisition failed (e.g. expired refresh) — the user re-signs in
    // interactively from the login page. Leave the store unpopulated.
  }
}

/** Begin interactive sign-in (login page button). */
export async function signIn(): Promise<void> {
  if (!isMsalConfigured()) return;
  await ensureInitialized();
  await getInstance().loginRedirect({ scopes: [apiScope] });
}

/** Sign out of both the local store and Entra. */
export async function signOut(): Promise<void> {
  useAuth.getState().clear();
  if (!isMsalConfigured()) return;
  await ensureInitialized();
  await getInstance().logoutRedirect();
}

/**
 * Return a valid API access token, refreshing via MSAL when needed. Called by
 * the API client before attaching the bearer header. Falls back to the stored
 * token for the dev-auth / magic-link paths (MSAL not configured).
 */
export async function ensureFreshApiToken(): Promise<string | null> {
  if (!isMsalConfigured()) return getAuthToken();
  await ensureInitialized();
  const account = getInstance().getActiveAccount() ?? getInstance().getAllAccounts()[0] ?? null;
  if (!account) {
    // MSAL is the auth authority in configured builds. No account means
    // whatever token the store persisted is orphaned (and almost
    // certainly expired) — clear it so RequireAuth routes to sign-in
    // instead of the app sending dead bearers at the API.
    if (getAuthToken()) useAuth.getState().clear();
    return null;
  }
  try {
    const result = await getInstance().acquireTokenSilent({ scopes: [apiScope], account });
    populateSession(result.accessToken, account);
    return result.accessToken;
  } catch {
    // Silent refresh failed (revoked / expired refresh token, conditional
    // access, etc.). Serving the stored token would just 401 — clear the
    // session so the user lands on sign-in with a clean state.
    useAuth.getState().clear();
    return null;
  }
}
